/**
 * The icon set.
 *
 * Inline SVG, not emoji. Emoji render differently on every operating system, cannot
 * be coloured or sized to match the design, and are announced unpredictably by
 * screen readers ("page facing up" for a document icon).
 *
 * Every icon is a 24x24 stroke path on the same grid, so they sit together without
 * visual jitter, and uses `currentColor` so one file serves active navigation,
 * danger buttons and muted help text alike.
 *
 * Ported from `app/ui/icons.py` deliberately: the two front ends should look like
 * the same product while both exist.
 */
/* oxlint-disable react/only-export-components -- This module intentionally exports a stable icon component registry for dynamic lookup. */

type Props = { size?: number; className?: string }

function Svg({ size = 18, className, children }: Props & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {children}
    </svg>
  )
}

export const Icon = {
  schools: (p: Props) => (
    <Svg {...p}>
      <path d="M3 21h18" />
      <path d="M5 21V9l7-5 7 5v12" />
      <path d="M9 21v-5h6v5" />
      <path d="M8 11h.01M12 11h.01M16 11h.01" />
    </Svg>
  ),
  home: (p: Props) => (
    <Svg {...p}>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5.5 10v10h13V10" />
      <path d="M9.5 20v-6h5v6" />
    </Svg>
  ),
  papers: (p: Props) => (
    <Svg {...p}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6" />
      <path d="M9 17h4" />
    </Svg>
  ),
  pictures: (p: Props) => (
    <Svg {...p}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.6" />
      <path d="M21 16l-5-5-4.5 4.5L9 13l-6 6" />
    </Svg>
  ),
  review: (p: Props) => (
    <Svg {...p}>
      <path d="M9 11l3 3 8-8" />
      <path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" />
    </Svg>
  ),
  printing: (p: Props) => (
    <Svg {...p}>
      <path d="M6 9V3h12v6" />
      <path d="M6 18H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1" />
      <rect x="6" y="14" width="12" height="7" rx="1" />
    </Svg>
  ),
  settings: (p: Props) => (
    <Svg {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2 2 2 0 1 1-4 0 1.7 1.7 0 0 0-2.9-1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 4.6 15a2 2 0 1 1 0-4 1.7 1.7 0 0 0 1.2-2.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 11.5 4.6a2 2 0 1 1 4 0 1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0 1.2 2.9 2 2 0 1 1 0 4 1.7 1.7 0 0 0-1.2 1z" />
    </Svg>
  ),
  collapse: (p: Props) => (
    <Svg {...p}>
      <path d="M15 18l-6-6 6-6" />
    </Svg>
  ),
  expand: (p: Props) => (
    <Svg {...p}>
      <path d="M9 18l6-6-6-6" />
    </Svg>
  ),
  signout: (p: Props) => (
    <Svg {...p}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </Svg>
  ),
  back: (p: Props) => (
    <Svg {...p}>
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </Svg>
  ),
  plus: (p: Props) => (
    <Svg {...p}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </Svg>
  ),
  edit: (p: Props) => (
    <Svg {...p}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4z" />
    </Svg>
  ),
  archive: (p: Props) => (
    <Svg {...p}>
      <path d="M3 6h18" />
      <path d="M5 6v14h14V6" />
      <path d="M4 3h16l1 3H3z" />
      <path d="M9 11h6" />
    </Svg>
  ),
  trash: (p: Props) => (
    <Svg {...p}>
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </Svg>
  ),
  download: (p: Props) => (
    <Svg {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5" />
      <path d="M12 15V3" />
    </Svg>
  ),
  undo: (p: Props) => (
    <Svg {...p}>
      <path d="M3 7v6h6" />
      <path d="M3 13a9 9 0 1 0 3-7.7L3 8" />
    </Svg>
  ),
  redo: (p: Props) => (
    <Svg {...p}>
      <path d="M21 7v6h-6" />
      <path d="M21 13a9 9 0 1 1-3-7.7L21 8" />
    </Svg>
  ),
  up: (p: Props) => (
    <Svg {...p}>
      <path d="M12 19V5" />
      <path d="M5 12l7-7 7 7" />
    </Svg>
  ),
  down: (p: Props) => (
    <Svg {...p}>
      <path d="M12 5v14" />
      <path d="M19 12l-7 7-7-7" />
    </Svg>
  ),
  copy: (p: Props) => (
    <Svg {...p}>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </Svg>
  ),
  warning: (p: Props) => (
    <Svg {...p}>
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
      <path d="M10.3 4.3 2.6 18a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0z" />
    </Svg>
  ),
  check: (p: Props) => (
    <Svg {...p}>
      <path d="M20 6L9 17l-5-5" />
    </Svg>
  ),
  audit: (p: Props) => (
    <Svg {...p}>
      <path d="M12 3l8 3v5c0 5-3.4 8.7-8 10-4.6-1.3-8-5-8-10V6z" />
      <path d="M9 11l2 2 4-4" />
      <path d="M9 17h6" />
    </Svg>
  ),
  menu: (p: Props) => (
    <Svg {...p}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </Svg>
  ),
  close: (p: Props) => (
    <Svg {...p}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Svg>
  ),
  search: (p: Props) => (
    <Svg {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.9-3.9" />
    </Svg>
  ),
  minus: (p: Props) => (
    <Svg {...p}>
      <path d="M5 12h14" />
    </Svg>
  ),
  /* Picture-library categories. Stroke glyphs on the same 24x24 grid as the rest,
     because these previously mixed full-colour emoji (paw, flower, car) with
     monochrome geometry in one list - and colour emoji ignore `currentColor`, so
     half the rail was violet line-art and half was full-colour clip art. */
  star: (p: Props) => (
    <Svg {...p}>
      <path d="m12 3.6 2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8L3.5 9.8l5.9-.9z" />
    </Svg>
  ),
  grid: (p: Props) => (
    <Svg {...p}>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1" />
    </Svg>
  ),
  paw: (p: Props) => (
    <Svg {...p}>
      <ellipse cx="12" cy="15.5" rx="4.2" ry="3.4" />
      <circle cx="6.4" cy="11.2" r="1.9" />
      <circle cx="17.6" cy="11.2" r="1.9" />
      <circle cx="9.6" cy="6.8" r="1.9" />
      <circle cx="14.4" cy="6.8" r="1.9" />
    </Svg>
  ),
  flower: (p: Props) => (
    <Svg {...p}>
      <circle cx="12" cy="9" r="2.2" />
      <path d="M12 6.8a2.4 2.4 0 1 1 2.2 2.2M14.2 9a2.4 2.4 0 1 1-2.2 2.2M12 11.2a2.4 2.4 0 1 1-2.2-2.2M9.8 9a2.4 2.4 0 1 1 2.2-2.2" />
      <path d="M12 13v8" />
    </Svg>
  ),
  toy: (p: Props) => (
    <Svg {...p}>
      <circle cx="12" cy="7" r="3" />
      <circle cx="6.5" cy="6.5" r="1.8" />
      <circle cx="17.5" cy="6.5" r="1.8" />
      <rect x="7.5" y="11" width="9" height="9" rx="3" />
    </Svg>
  ),
  car: (p: Props) => (
    <Svg {...p}>
      <path d="M4 15v-2.2L6 8h12l2 4.8V15" />
      <path d="M4 15h16v2.5h-2.5M4 15v2.5h2.5" />
      <circle cx="8" cy="17.5" r="1.8" />
      <circle cx="16" cy="17.5" r="1.8" />
    </Svg>
  ),
  shapes: (p: Props) => (
    <Svg {...p}>
      <path d="M8 3.5 13 12H3z" />
      <rect x="12" y="13" width="8.5" height="8" rx="1" />
      <circle cx="17" cy="7" r="3.4" />
    </Svg>
  ),
  pencil: (p: Props) => (
    <Svg {...p}>
      <path d="M4 20.5 4.9 17 16 5.9a2 2 0 0 1 2.8 0l1.3 1.3a2 2 0 0 1 0 2.8L9 21l-3.5.9z" />
      <path d="M14.5 7.5 17 10" />
    </Svg>
  ),
  eye: (p: Props) => (
    <Svg {...p}>
      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </Svg>
  ),
  eyeOff: (p: Props) => (
    <Svg {...p}>
      <path d="M10.6 6.1A9.6 9.6 0 0 1 12 5.5c6.4 0 10 6.5 10 6.5a17 17 0 0 1-3.2 4" />
      <path d="M6.2 8.3A16.7 16.7 0 0 0 2 12s3.6 6.5 10 6.5a9.9 9.9 0 0 0 4-.8" />
      <path d="m9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="M3 3l18 18" />
    </Svg>
  ),
  chevronUp: (p: Props) => (
    <Svg {...p}>
      <path d="m18 15-6-6-6 6" />
    </Svg>
  ),
  chevronDown: (p: Props) => (
    <Svg {...p}>
      <path d="m6 9 6 6 6-6" />
    </Svg>
  ),
  chevronRight: (p: Props) => (
    <Svg {...p}>
      <path d="m9 6 6 6-6 6" />
    </Svg>
  ),
  symbols: (p: Props) => (
    <Svg {...p}>
      <path d="M5 5h7L6 19h7" />
      <path d="M16 5h3M17.5 5v14M16 19h3" />
    </Svg>
  ),
}

/** Question type key -> icon. Unknown types fall back to a document rather than
 *  rendering nothing, so a newly added plugin still looks deliberate. */
export function questionIcon(type: string, size = 16) {
  const map: Record<string, keyof typeof Icon> = {
    multiple_choice: 'check',
    fill_in_blanks: 'papers',
    odd_one_out: 'pictures',
    matching: 'papers',
    colouring: 'pictures',
    true_false: 'check',
    short_answer: 'papers',
    numeric: 'papers',
    alphabet_handwriting: 'papers',
    number_sequence_grid: 'papers',
    missing_number: 'papers',
    count_pictures: 'pictures',
    compare_sizes: 'pictures',
    shape_properties: 'pictures',
    write_in_box: 'papers',
    tick_cross_pictures: 'check',
    drawing_response: 'papers',
  }
  const Chosen = Icon[map[type] ?? 'papers']
  return <Chosen size={size} />
}
